import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getAssetUrl } from '../../utils/helpers';
import PublicHeader from '../../components/PublicHeader';
import PublicFooter from '../../components/PublicFooter';
import '../../about-us-style.css';

const IconLive = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 28, height: 28 }}>
    <circle cx="12" cy="12" r="10" /><polygon points="10 8 16 12 10 16 10 8" fill="currentColor" stroke="none" />
  </svg>
);
const IconBook = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 28, height: 28 }}>
    <path d="M4 19.5A2.5 2.5 0 016.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" />
  </svg>
);
const IconLayers = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 28, height: 28 }}>
    <polygon points="12 2 2 7 12 12 22 7 12 2" /><polyline points="2 17 12 22 22 17" /><polyline points="2 12 12 17 22 12" />
  </svg>
);
const IconUser = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 28, height: 28 }}>
    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87" /><path d="M16 3.13a4 4 0 010 7.75" />
  </svg>
);
const IconMonitor = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 28, height: 28 }}>
    <rect x="2" y="3" width="20" height="14" rx="2" ry="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" />
  </svg>
);
const IconCheck = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 28, height: 28 }}>
    <path d="M22 11.08V12a10 10 0 11-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);
const IconMapPin = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 28, height: 28 }}>
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" />
  </svg>
);

const PrincipleCard = ({ icon, title, desc }) => (
  <div className="au-principle-card">
    <div className="au-card-icon">{icon}</div>
    <h3 className="au-card-title">{title}</h3>
    <p className="au-card-desc">{desc}</p>
  </div>
);
const ApproachCard = ({ icon, title, desc }) => (
  <div className="au-approach-card">
    <div className="au-approach-icon">{icon}</div>
    <div>
      <h3 className="au-approach-title">{title}</h3>
      <p className="au-approach-desc">{desc}</p>
    </div>
  </div>
);
const ProgramCard = ({ title, desc, to }) => (
  <Link to={to} className="au-program-card">
    <div className="au-program-dot" aria-hidden="true" />
    <div>
      <div className="au-program-title">{title}</div>
      <p className="au-program-desc">{desc}</p>
    </div>
    <span className="au-program-arrow" aria-hidden="true">→</span>
  </Link>
);
const StepCard = ({ num, title, desc }) => (
  <div className="au-step-card">
    <div className="au-step-num">{num}</div>
    <div>
      <h3 className="au-step-title">{title}</h3>
      <p className="au-step-desc">{desc}</p>
    </div>
  </div>
);
const ValueCard = ({ title, desc }) => (
  <div className="au-value-card">
    <span className="au-value-dot" aria-hidden="true">◆</span>
    <div>
      <h3 className="au-value-title">{title}</h3>
      <p className="au-value-desc">{desc}</p>
    </div>
  </div>
);

const AboutUs = () => {
  useEffect(() => {
    document.title = 'About PranaFlow Academy | Yoga Education in Rishikesh';
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) { metaDesc = document.createElement('meta'); metaDesc.setAttribute('name', 'description'); document.head.appendChild(metaDesc); }
    metaDesc.setAttribute('content', 'Learn about PranaFlow Academy, a yoga education and wellness training academy based in Rishikesh, India. Discover our teaching approach, values, programs, certification transparency and commitment to structured learning.');
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); canonical.setAttribute('rel', 'canonical'); document.head.appendChild(canonical); }
    canonical.setAttribute('href', 'https://pranaflowacademy.com/about-us');
    const ogTags = [
      { property: 'og:title', content: 'About PranaFlow Academy | Yoga Education in Rishikesh' },
      { property: 'og:description', content: 'Learn about PranaFlow Academy, our Rishikesh roots, teaching philosophy, yoga education programs and commitment to structured, transparent learning.' },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: 'https://pranaflowacademy.com/about-us' },
      { property: 'og:image', content: 'https://pranaflowacademy.com/assets/rishikesh_yoga.webp' },
      { property: 'og:site_name', content: 'PranaFlow Academy' }
    ];
    ogTags.forEach(tag => {
      let el = document.querySelector(`meta[property="${tag.property}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute('property', tag.property); document.head.appendChild(el); }
      el.setAttribute('content', tag.content);
    });
    const twitterTags = [
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: 'About PranaFlow Academy | Yoga Education in Rishikesh' },
      { name: 'twitter:description', content: 'Learn about PranaFlow Academy, our Rishikesh roots, teaching philosophy, programs and commitment to structured yoga education.' },
      { name: 'twitter:image', content: 'https://pranaflowacademy.com/assets/rishikesh_yoga.webp' }
    ];
    twitterTags.forEach(tag => {
      let el = document.querySelector(`meta[name="${tag.name}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute('name', tag.name); document.head.appendChild(el); }
      el.setAttribute('content', tag.content);
    });
    const scriptId = 'jsonld-schema-about-us';
    let script = document.getElementById(scriptId);
    if (!script) { script = document.createElement('script'); script.id = scriptId; script.type = 'application/ld+json'; document.head.appendChild(script); }
    const aboutPageSchema = {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      '@id': 'https://pranaflowacademy.com/about-us#webpage',
      'url': 'https://pranaflowacademy.com/about-us',
      'name': 'About PranaFlow Academy | Yoga Education in Rishikesh',
      'description': 'Learn about PranaFlow Academy, a yoga education and wellness training academy based in Rishikesh, India. Discover our teaching approach, values, programs, and commitment to structured learning.',
      'isPartOf': { '@id': 'https://pranaflowacademy.com/#website' },
      'about': { '@id': 'https://pranaflowacademy.com/#organization' }
    };
    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      'itemListElement': [
        { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': 'https://pranaflowacademy.com/' },
        { '@type': 'ListItem', 'position': 2, 'name': 'About Us', 'item': 'https://pranaflowacademy.com/about-us' }
      ]
    };
    script.textContent = JSON.stringify([aboutPageSchema, breadcrumbSchema]);
    window.scrollTo(0, 0);
    return () => { const existing = document.getElementById(scriptId); if (existing) existing.remove(); };
  }, []);

  return (
    <div className="public-site" style={{ backgroundColor: 'var(--bg-base)' }}>
      <PublicHeader />

      {/* SECTION 1 — HERO */}
      <section className="au-hero" aria-label="About PranaFlow Academy">
        <div className="container au-hero-grid">
          <div className="au-hero-content">
            <span className="section-meta" style={{ marginBottom: '16px', display: 'inline-block' }}>ABOUT PRANAFLOW ACADEMY</span>
            <h1 className="au-hero-h1">Yoga Education Rooted in Rishikesh, Designed for Modern Learners</h1>
            <p className="au-hero-intro">
              PranaFlow Academy is a yoga education and wellness training academy based in{' '}
              <strong>Rishikesh, Uttarakhand, India</strong> — a city known worldwide for its deep
              connection with yoga. We bring structured learning, experienced teachers, live
              instruction, practical training, and accessible online education together to help
              students build meaningful knowledge and confident teaching skills.
            </p>
            <p className="au-hero-sub">
              From foundational Yoga Teacher Training to specialized programs in Kids Yoga, Face
              Yoga, Diet &amp; Nutrition, and holistic wellness, our focus is on clear teaching,
              disciplined learning, practical application, and responsible certification.
            </p>
            <div className="au-hero-btns">
              <Link to="/courses" className="btn btn-primary">Explore Our Courses</Link>
              <Link to="/demo-class" className="btn btn-secondary">Start with a Demo Class</Link>
            </div>
          </div>
          <div className="au-hero-media">
            <div className="au-hero-img-wrap">
              <img src={getAssetUrl('assets/pranaflow_hero_rishikesh.webp')} alt="Yoga Teacher Training in Rishikesh, India by the Ganges" className="au-hero-img" width="640" height="480" />
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2 — WHO WE ARE */}
      <section className="au-who section-padding" style={{ backgroundColor: 'var(--bg-surface-tint)' }}>
        <div className="container au-who-grid">
          <div className="au-who-media">
            <img src={getAssetUrl('assets/pranaflow_teaching_philosophy.webp')} alt="Yoga Teacher Training and Philosophy in Rishikesh India" className="au-who-img" width="560" height="560" />
          </div>
          <div className="au-who-content">
            <span className="section-meta">WHO WE ARE</span>
            <h2 className="section-title au-section-title">More Than a Course Platform — A Structured Yoga Learning Academy</h2>
            <p className="au-body-text">PranaFlow Academy was created to make structured yoga and wellness education accessible to students who want more than short videos or unstructured online lessons.</p>
            <p className="au-body-text">Our programs combine live instruction, guided practice, academic learning, teacher interaction, practical assignments, assessments, and student dashboard support so learners can progress through a defined educational pathway.</p>
            <p className="au-body-text">Our roots are in Rishikesh, India, while our learning environment is designed to serve students wherever they are. This allows learners to study with experienced teachers and structured course material without losing the discipline, depth, and personal interaction that meaningful yoga education requires.</p>
            <p className="au-body-text">We believe that yoga education should be clear, responsible, practical, and transparent. Students should understand what they are studying, who is teaching them, how their training is delivered, what certificate they receive, and what that certificate represents.</p>
          </div>
        </div>
      </section>

      {/* SECTION 3 — OUR PURPOSE */}
      <section className="au-purpose section-padding">
        <div className="container">
          <div className="au-section-header">
            <span className="section-meta">OUR PURPOSE</span>
            <h2 className="section-title au-section-title">Making Authentic Yoga Education More Accessible</h2>
            <p className="au-section-intro">Our purpose is to create an environment where students can study yoga and wellness subjects with structure, guidance, consistency, and professional support.</p>
          </div>
          <div className="au-principles-grid">
            <PrincipleCard icon={<IconLayers />} title="Structured Learning" desc="Courses are organized into defined subjects, modules, live classes, practical work, and assessments rather than disconnected lessons." />
            <PrincipleCard icon={<IconUser />} title="Experienced Guidance" desc="Students learn through live interaction with teachers and subject professionals appropriate to each program." />
            <PrincipleCard icon={<IconCheck />} title="Practical Application" desc="Training is designed to help students understand how knowledge can be applied in personal practice, teaching, and responsible wellness education." />
            <PrincipleCard icon={<IconBook />} title="Transparent Certification" desc="We clearly explain who issues each certificate, what the training represents, and where partner-school credentials apply." />
          </div>
        </div>
      </section>

      {/* SECTION 4 — RISHIKESH */}
      <section className="au-rishikesh section-padding" style={{ backgroundColor: 'var(--bg-surface-tint)' }}>
        <div className="container au-rishikesh-inner">
          <div className="au-rishikesh-badge"><IconMapPin /></div>
          <span className="section-meta">OUR ROOTS</span>
          <h2 className="section-title au-section-title" style={{ maxWidth: 720, margin: '12px auto 24px', textAlign: 'center' }}>Rooted in Rishikesh — A City Closely Connected with the Tradition of Yoga</h2>
          <div className="au-rishikesh-body">
            <p className="au-body-text">PranaFlow Academy is based in <strong>Rishikesh, Uttarakhand, India</strong>.</p>
            <p className="au-body-text">For generations, Rishikesh has been associated with yoga practice, spiritual study, meditation, ashram traditions, and teacher education. Being connected to this environment influences how we think about yoga education — not simply as physical exercise, but as a disciplined field that includes philosophy, anatomy, breathwork, meditation, teaching methodology, ethics, and self-practice.</p>
            <p className="au-body-text">At the same time, PranaFlow Academy recognizes that modern students need flexibility and accessibility. Our live online learning model helps connect the educational spirit of Rishikesh with students across different locations.</p>
          </div>
        </div>
      </section>

      {/* SECTION 5 — EDUCATIONAL APPROACH */}
      <section className="au-approach section-padding">
        <div className="container">
          <div className="au-section-header">
            <span className="section-meta">HOW WE TEACH</span>
            <h2 className="section-title au-section-title">Traditional Foundations, Structured for Today's Students</h2>
          </div>
          <div className="au-approach-grid">
            <ApproachCard icon={<IconLive />} title="Live Learning" desc="Students learn through scheduled live sessions that allow direct interaction, clarification, discussion, and guided practice." />
            <ApproachCard icon={<IconBook />} title="Theory + Practice" desc="Training combines conceptual understanding with physical practice, observation, assignments, and teaching application." />
            <ApproachCard icon={<IconLayers />} title="Progressive Curriculum" desc="Programs move from foundational concepts toward deeper or specialized subjects in a structured sequence." />
            <ApproachCard icon={<IconUser />} title="Teacher Interaction" desc="Students are able to learn from instructors rather than relying only on pre-recorded material." />
            <ApproachCard icon={<IconMonitor />} title="Student Dashboard" desc="Where applicable, enrolled students receive access to their PranaFlow Academy dashboard for course-related learning and academic support." />
            <ApproachCard icon={<IconCheck />} title="Assessment &amp; Completion" desc="Programs may include attendance requirements, assignments, practical evaluation, or final assessments depending on the course." />
          </div>
        </div>
      </section>

      {/* SECTION 6 — COURSES */}
      <section className="au-programs section-padding" style={{ backgroundColor: 'var(--bg-surface-tint)' }}>
        <div className="container">
          <div className="au-section-header">
            <span className="section-meta">OUR COURSES</span>
            <h2 className="section-title au-section-title">Learning Pathways for Yoga Teachers, Practitioners and Wellness Students</h2>
            <p className="au-section-intro">PranaFlow Academy offers foundational, advanced, specialized, and wellness-focused learning pathways.</p>
          </div>
          <div className="au-programs-grid">
            <ProgramCard title="200 Hour Yoga Teacher Training" desc="Foundation-level yoga teacher education covering asana, pranayama, philosophy, anatomy, meditation, alignment, sequencing, and teaching methodology." to="/courses/200-hour-yoga-teacher-training-rishikesh" />
            <ProgramCard title="300 Hour Yoga Teacher Training" desc="Advanced training for students seeking deeper study of yoga practice, philosophy, anatomy, sequencing, and professional teaching skills." to="/courses/300hr-yoga-ttc" />
            <ProgramCard title="500 Hour Yoga Teacher Training" desc="A comprehensive learning pathway designed for students seeking extensive yoga education across foundational and advanced areas." to="/courses/500hr-yoga-ttc" />
            <ProgramCard title="95-Hour Kids Yoga Teacher Training" desc="Specialized education focused on age-appropriate yoga, mindfulness, breathing, storytelling, safety, creativity, and teaching methodology for children." to="/courses/95-hour-kids-yoga-teacher-training" />
            <ProgramCard title="Face Yoga Teacher Training" desc="Live specialized training combining facial anatomy, facial exercises, massage techniques, natural wellness practices, and instructor methodology." to="/courses/face-yoga-teacher-training-course-online" />
            <ProgramCard title="Diet &amp; Nutrition Course" desc="Structured nutrition education covering practical food knowledge, meal planning, nutritional principles, and holistic wellness education." to="/courses/diet-and-nutrition-course-online" />
            <ProgramCard title="PCOS &amp; PCOD Management Program" desc="A structured wellness education program combining lifestyle, nutrition, yoga, exercise, stress-management, sleep, and menstrual-health education." to="/courses/pcos-pcod-management-program" />
          </div>
          <div className="au-programs-cta">
            <Link to="/courses" className="btn btn-primary">View All Courses</Link>
          </div>
        </div>
      </section>

      {/* SECTION 7 — JOURNEY */}
      <section className="au-journey section-padding">
        <div className="container">
          <div className="au-section-header">
            <StepCard num="04" title="Complete Your Training" desc="Students who successfully meet the relevant program completion requirements receive the certificate applicable to that course." />
          </div>
        </div>
      </section>

      {/* SECTION 8 — COMMITMENT */}
      <section className="au-commitment section-padding" style={{ backgroundColor: 'var(--bg-surface-tint)' }}>
        <div className="container au-commitment-grid">
          <div className="au-commitment-content">
            <span className="section-meta">OUR COMMITMENT</span>
            <h2 className="section-title au-section-title">Clear Education. Responsible Claims. Transparent Certification.</h2>
            <p className="au-body-text">At PranaFlow Academy, we believe trust must be supported by transparency.</p>
            <p className="au-body-text">Before students enroll, we aim to clearly communicate:</p>
            <ul className="au-commitment-list">
              <li>Course duration and learning format</li>
              <li>Syllabus and class structure</li>
              <li>Fees and batch information</li>
              <li>Certificate type and assessment requirements</li>
              <li>Program scope and applicable limitations</li>
            </ul>
            <p className="au-body-text" style={{ marginTop: '20px' }}>We also distinguish yoga and wellness education from regulated medical care. Programs involving nutrition, PCOS/PCOD wellness, Face Yoga, or other health-related subjects are educational in nature and do not replace diagnosis, treatment, or professional medical care.</p>
          </div>
          <div className="au-commitment-visual">
            <img src={getAssetUrl('assets/pranaflow_commitment_rishikesh.webp')} alt="Yoga practitioner meditating in Rishikesh with the Ganges and Himalayas in the background at PranaFlow Academy" className="au-commitment-img" width="520" height="400" />
          </div>
        </div>
      </section>

      {/* SECTION 9 — YOGA ALLIANCE */}
      <section className="au-ya section-padding">
        <div className="container au-ya-inner">
          <span className="section-meta">CERTIFICATION TRANSPARENCY</span>
          <h2 className="section-title au-section-title" style={{ maxWidth: 680, margin: '12px auto 24px', textAlign: 'center' }}>How Yoga Alliance Partner Credentials Are Handled</h2>
          <div className="au-ya-body">
            <p className="au-body-text">For programs where Yoga Alliance-related credentials apply, PranaFlow Academy works with appropriately registered partner schools.</p>
            <p className="au-body-text">Before establishing such partnerships, we verify the relevant school's Yoga Alliance registration and review its suitability for the applicable training pathway.</p>
            <p className="au-body-text">The specific certificate and credential pathway can vary by program, and students should refer to the certification section of the individual course page for the exact details applicable to their training.</p>
            <p className="au-body-text">PranaFlow Academy does not present every course as Yoga Alliance accredited. Specialized programs that are not covered by a Yoga Alliance credential are clearly identified as PranaFlow Academy certificates or certificates of completion, as applicable.</p>
          </div>
          <div className="au-ya-programs-note">
            <span className="badge" style={{ fontSize: '0.85rem' }}>For program-specific certification details, refer to the individual course pages</span>
          </div>
        </div>
      </section>

      {/* SECTION 10 — WHY PRANAFLOW */}
      <section className="au-why section-padding" style={{ backgroundColor: 'var(--bg-surface-tint)' }}>
        <div className="container">
          <div className="au-section-header">
            <span className="section-meta">WHY PRANAFLOW</span>
            <h2 className="section-title au-section-title">A Learning Environment Built Around Clarity and Support</h2>
          </div>
          <div className="au-why-grid">
            <div className="au-why-card"><div className="au-why-icon"><IconLive /></div><h3 className="au-why-title">Live Teacher-Led Training</h3><p className="au-why-desc">Real-time instruction and interaction rather than a purely self-paced video library.</p></div>
            <div className="au-why-card"><div className="au-why-icon"><IconLayers /></div><h3 className="au-why-title">Structured Curriculum</h3><p className="au-why-desc">Clearly organized subjects, modules, progression, and learning expectations.</p></div>
            <div className="au-why-card"><div className="au-why-icon"><IconUser /></div><h3 className="au-why-title">Small-Batch Learning</h3><p className="au-why-desc">Where applicable, batch sizes are kept structured to support interaction and teacher attention.</p></div>
            <div className="au-why-card"><div className="au-why-icon"><IconBook /></div><h3 className="au-why-title">Practical Education</h3><p className="au-why-desc">Programs combine knowledge with practical understanding, guided activities, and relevant assessments.</p></div>
            <div className="au-why-card"><div className="au-why-icon"><IconMonitor /></div><h3 className="au-why-title">Student Learning Portal</h3><p className="au-why-desc">Enrolled students can access applicable academic resources and course information through the PranaFlow Academy LMS.</p></div>
            <div className="au-why-card"><div className="au-why-icon"><IconMapPin /></div><h3 className="au-why-title">Rishikesh Identity</h3><p className="au-why-desc">Education shaped by an academy based in one of India's most recognized yoga destinations.</p></div>
          </div>
        </div>
      </section>

      {/* SECTION 11 — VALUES */}
      <section className="au-values section-padding">
        <div className="container">
          <div className="au-section-header">
            <span className="section-meta">WHAT GUIDES US</span>
            <h2 className="section-title au-section-title">The Values Behind Our Academy</h2>
          </div>
          <div className="au-values-grid">
            <ValueCard title="Authenticity" desc="Respect yoga as a discipline with physical, philosophical, ethical, and educational dimensions." />
            <ValueCard title="Responsibility" desc="Avoid misleading medical, career, or certification claims." />
            <ValueCard title="Accessibility" desc="Make structured education available to students who cannot relocate for full-time residential training." />
            <ValueCard title="Discipline" desc="Meaningful learning requires attendance, practice, study, consistency, and participation." />
            <ValueCard title="Transparency" desc="Students deserve clear information about programs, fees, teaching formats, certificates, and expectations." />
            <ValueCard title="Growth" desc="Education should help students deepen personal understanding while developing the skills needed for responsible teaching and practice." />
          </div>
        </div>
      </section>

      {/* SECTION 12 — CTA */}
      <section className="au-cta section-padding">
        <div className="container au-cta-inner">
          <h2 className="au-cta-heading">Begin Your Learning Journey with PranaFlow Academy</h2>
          <p className="au-cta-text">Explore our yoga teacher training and wellness education programs and choose the learning pathway that best matches your present goals.</p>
          <div className="au-cta-btns">
            <Link to="/courses" className="btn btn-primary">Explore Our Courses</Link>
            <Link to="/demo-class" className="btn btn-secondary">Join a Demo Class</Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default AboutUs;
